/**
 * Parent request workflow: stage rail + per-status content panes
 * (Submitted → … → Published), mirroring the IT scan panel pattern.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, ExternalLink, Paperclip, Trash2 } from 'lucide-react';
import config from '../../../config/environment';
import garageAPI from '../../../services/garage/api';
import {
  FORWARD_LABELS,
  canEscalateBizOps,
  canHold,
  canPublishFromStatus,
  canReject,
  canRequestInfo,
  canRouteToIT,
  earlierPipelineStages,
  forwardTargets,
  isAlreadyRoutedToIT,
  isGemRequest,
  isSalesPersona,
  normalizeStatus as normalizeStatusShared,
  resolveWorkflowRail,
  stageLabel,
  visiblePipelineStages,
} from '../../../utils/ideaStatusTransitions';
import { formatCommentHtml } from '../../../utils/formatCommentHtml';
import { formatAttachmentSize } from './RequestAttachmentPicker';
import ItScanFlowPanel from './ItScanFlowPanel';
import './RequestWorkflowPanel.css';

const REQUEST_PHASE_LABELS = {
  'active-development': 'Active Development Phase',
  'existing-enhancement': 'Feature / Optimization Request',
  ideation: 'Ideation & Design Phase',
};

const appTypeLabel = (appType) => {
  if (appType === 'gem') return 'Gem';
  if (appType === 'app') return 'App';
  return null;
};

const WORKFLOW_STAGES = [
  { id: 'submitted', label: 'Submitted' },
  { id: 'under_review', label: 'Under review' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'optimization', label: 'Optimization' },
  { id: 'escalated_to_it_bizops', label: 'IT BizOps' },
  { id: 'it_review', label: 'IT review' },
  { id: 'deploying', label: 'Deploying' },
  { id: 'published', label: 'Published' },
];

const PAUSED_STATUS_STAGE = {
  needs_info: 'under_review',
  'needs-info': 'under_review',
  on_hold: 'under_review',
  rejected: 'under_review',
};

const STATUS_ALIASES = {
  'under-review': 'under_review',
  'in-progress': 'in_progress',
  'needs-info': 'needs_info',
  'it-review': 'it_review',
  'routed-to-it': 'it_review',
};

const REACH_LABELS = {
  my_team: 'My team (under 10)',
  department: 'Department (10-50)',
  function: 'Function (50-500)',
  company: 'Company-wide (500+)',
};

const normalizeStatus = (status) => normalizeStatusShared(status) || (STATUS_ALIASES[status] || status || '');

const stageIndex = (status) => {
  const normalized = normalizeStatus(status);
  const idx = WORKFLOW_STAGES.findIndex((s) => s.id === normalized);
  if (idx >= 0) return idx;
  const held = PAUSED_STATUS_STAGE[normalized];
  if (held) return WORKFLOW_STAGES.findIndex((s) => s.id === held);
  return -1;
};

const isPausedStatus = (status) => {
  const n = normalizeStatus(status);
  return n === 'needs_info' || n === 'on_hold' || n === 'rejected';
};

const needsItScanPanel = (data) =>
  Boolean(
    data &&
      (data.status === 'it_review' ||
        data.status === 'routed-to-it' ||
        data.status === 'deploying' ||
        data.secScanRepoId ||
        data.itSubflowNode ||
        data.itScansNotApplicable ||
        data.itInputRequired)
  );

const resolveTimeline = (request, activity = []) =>
  resolveWorkflowRail(request, {
    activity,
    stages: visiblePipelineStages(request),
  });

const activeStageId = (timeline, request) => {
  const active = timeline.find((s) => s.state === 'active');
  if (active) return active.id;
  const idx = stageIndex(request?.status);
  if (idx >= 0) return WORKFLOW_STAGES[idx].id;
  return 'submitted';
};

const pillTone = (state) => {
  if (state === 'active') return 'tone-active';
  if (state === 'completed') return 'tone-ok';
  return 'tone-pending';
};

const pillLabel = (state) => {
  if (state === 'active') return 'Current';
  if (state === 'completed') return 'Completed';
  return 'Not reached';
};

const truncate = (text, max = 280) => {
  const t = String(text || '').trim();
  if (!t) return '';
  if (t.length <= max) return t;
  return `${t.slice(0, max).trim()}…`;
};

/** Comments handed into this stage (not status-history sentences — those live in Activity). */
const HANDOFF_COMMENT_KINDS = new Set([
  'comment',
  'stage_transition',
  'info_request',
  'info_response',
  'routed_to_it',
  'escalated_to_dot',
  'system',
]);

const LABEL_TO_STAGE_ID = Object.fromEntries(
  WORKFLOW_STAGES.map((s) => [s.label.toLowerCase(), s.id]).concat([
    ['more info needed', 'needs_info'],
    ['on hold', 'on_hold'],
    ['it bizops', 'escalated_to_it_bizops'],
  ])
);

/** Destination stage id from "**Stage change:** From → To" handoff header. */
const destinationStageFromText = (raw) => {
  const first = String(raw || '')
    .trim()
    .split(/\n/)[0]
    .replace(/\*{1,2}/g, '')
    .trim();
  const match = first.match(/^Stage(?:\s+change)?:\s*(.+?)(?:\s*→\s*(.+))?$/i);
  if (!match) return '';
  const toLabel = (match[2] || match[1] || '').trim().toLowerCase();
  if (!toLabel) return '';
  return (
    LABEL_TO_STAGE_ID[toLabel] ||
    normalizeStatus(toLabel.replace(/\s+/g, '_').replace(/-/g, '_'))
  );
};

const stageHandoffComments = (activity, stageId) => {
  if (!Array.isArray(activity) || !stageId) return [];
  return activity
    .filter((e) => {
      const kind = e.kind || '';
      // Status-history sentences live in the bottom feed only
      if (kind === 'status_change' || kind === 'created' || kind === 'attachment') {
        return false;
      }
      const body = String(e.text || '').trim();
      if (!body) return false;
      const isCommentLike = HANDOFF_COMMENT_KINDS.has(kind) || Boolean(body);
      if (!isCommentLike) return false;
      // Prefer destination parsed from the handoff header (authoritative)
      const fromHeader = destinationStageFromText(body);
      const to = fromHeader || normalizeStatus(e.toStage || e.to_stage);
      return to === stageId;
    })
    .slice(0, 6);
};

function ReviewerBlock({ reviewerData }) {
  if (!reviewerData) return null;
  return (
    <div className="request-flow-reviewer">
      {reviewerData.team ? (
        <div className="request-flow-reviewer-team">Team: {reviewerData.team}</div>
      ) : null}
      {reviewerData.name ? (
        <div className="request-flow-reviewer-name">{reviewerData.name}</div>
      ) : null}
      {reviewerData.status_text ? (
        <div className="request-flow-reviewer-status">{reviewerData.status_text}</div>
      ) : null}
    </div>
  );
}

/** Split auto-prefixed stage header from the real note body. */
const parseHandoffBody = (raw) => {
  const text = String(raw || '').trim();
  if (!text) return { path: null, note: '' };

  const lines = text.split(/\n+/);
  const first = (lines[0] || '').replace(/\*{1,2}/g, '').trim();
  const match = first.match(/^Stage(?:\s+change)?:\s*(.+?)(?:\s*→\s*(.+))?$/i);

  if (match) {
    const from = (match[1] || '').trim();
    const to = (match[2] || '').trim();
    const path = to ? { from, to } : from ? { from: null, to: from } : null;
    const note = lines.slice(1).join('\n').trim();
    return { path, note };
  }

  // Fallback: strip leftover ** from display via formatCommentHtml
  return { path: null, note: text };
};

function HandoffComments({ entries, emptyHint = 'No handoff comment from the previous stage.' }) {
  if (!entries.length) {
    return <p className="request-flow-muted">{emptyHint}</p>;
  }
  return (
    <ul className="request-flow-handoff-list">
      {entries.map((e, i) => {
        const { path, note } = parseHandoffBody(e.text);
        if (!path && !note) return null;
        const who = e.actorName || e.actorEmail;
        return (
          <li key={e.id || `handoff-${i}`} className="request-flow-handoff-card">
            <div className="request-flow-handoff-meta">
              {who ? <span className="request-flow-handoff-author">{who}</span> : null}
              {path ? (
                <span className="request-flow-handoff-path" aria-label="Stage change">
                  {path.from ? <span className="request-flow-handoff-stage">{path.from}</span> : null}
                  {path.from && path.to ? (
                    <span className="request-flow-handoff-arrow" aria-hidden>
                      →
                    </span>
                  ) : null}
                  {path.to ? (
                    <span className="request-flow-handoff-stage is-to">{path.to}</span>
                  ) : null}
                </span>
              ) : null}
            </div>
            {note ? (
              <div
                className="request-flow-handoff-body"
                dangerouslySetInnerHTML={{ __html: formatCommentHtml(note) }}
              />
            ) : (
              <p className="request-flow-muted" style={{ margin: 0 }}>
                No additional note.
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function PaneShell({ title, state, children, actions }) {
  return (
    <div className="request-flow-pane">
      <div className="request-flow-pane-head">
        <h4>{title}</h4>
        <span className={`request-flow-status-pill ${pillTone(state)}`}>{pillLabel(state)}</span>
      </div>
      {children}
      {actions ? <div className="request-flow-actions">{actions}</div> : null}
    </div>
  );
}

const RequestWorkflowPanel = ({
  request,
  requestId,
  activity = [],
  attachments = [],
  itReviewPayload = null,
  submittedDate = '',
  canManageRequests = false,
  canEscalateToDot = false,
  canPublishGem = false,
  isGemPublished = false,
  userEmail,
  userName,
  userRole,
  downloadingAttachment = null,
  onDownloadAttachment,
  onDeleteAttachment,
  onStatusChange,
  onRequestInfo,
  onRouteToIT,
  onEscalateToDot,
  onPublishGem,
  onRequestRefresh,
  actionsDisabled = false,
  /** Jump to IT review → Initiated (requestor input / banner CTA). */
  focusItInitiated = false,
}) => {
  const timeline = useMemo(
    () => resolveTimeline(request, activity),
    [request, activity]
  );
  const liveStage = useMemo(() => activeStageId(timeline, request), [timeline, request]);
  const followActiveRef = useRef(true);
  const [selectedStage, setSelectedStage] = useState(
    focusItInitiated ? 'it_review' : liveStage
  );
  const [artifactBusy, setArtifactBusy] = useState(null);

  useEffect(() => {
    if (followActiveRef.current) {
      setSelectedStage(liveStage);
    }
  }, [liveStage]);

  useEffect(() => {
    if (!focusItInitiated) return;
    followActiveRef.current = false;
    setSelectedStage('it_review');
  }, [focusItInitiated, requestId]);

  const selectedMeta = timeline.find((s) => s.id === selectedStage) || {
    id: selectedStage,
    label: WORKFLOW_STAGES.find((w) => w.id === selectedStage)?.label || selectedStage,
    state: 'pending',
    reviewerData: null,
  };
  const isActivePane = selectedMeta.state === 'active' || selectedStage === liveStage;
  const paused = isPausedStatus(request?.status);
  // IT subflow (incl. its own Deployment node) only on IT review — not on parent Deploying
  const showItEmbed = selectedStage === 'it_review' && needsItScanPanel(request);

  const selectStage = (id) => {
    followActiveRef.current = id === liveStage;
    setSelectedStage(id);
  };

  const liveStatus = normalizeStatus(request?.status);
  const actionsLiveHere =
    canManageRequests &&
    isActivePane &&
    (selectedStage === liveStage ||
      (paused && selectedStage === 'under_review'));

  const adminBtn = (label, onClick, opts = {}) => {
    if (!actionsLiveHere) return null;
    const { disabled, className = 'garage-btn garage-btn-secondary garage-btn-sm', title } = opts;
    return (
      <button
        type="button"
        className={className}
        onClick={onClick}
        disabled={Boolean(disabled || actionsDisabled)}
        title={title}
      >
        {label}
      </button>
    );
  };

  const renderSendBackControl = () => {
    if (!actionsLiveHere) return null;
    const earlier = earlierPipelineStages(liveStatus, request);
    if (!earlier.length) return null;
    return (
      <label className="request-flow-sendback">
        <span className="request-flow-sendback-label">Send back</span>
        <select
          className="request-flow-sendback-select"
          defaultValue=""
          disabled={actionsDisabled}
          onChange={(e) => {
            const v = e.target.value;
            e.target.value = '';
            if (v) onStatusChange?.(v);
          }}
          aria-label="Send back to earlier stage"
        >
          <option value="" disabled>
            Return to…
          </option>
          {earlier.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
    );
  };

  const renderCommonSideActions = () => (
    <>
      {canRequestInfo(liveStatus)
        ? adminBtn('Request info', () => onRequestInfo?.())
        : null}
      {canHold(liveStatus)
        ? adminBtn('Put on hold', () => onStatusChange?.('on_hold'))
        : null}
      {canReject(liveStatus)
        ? adminBtn('Reject', () => onStatusChange?.('rejected'), {
            className: 'garage-btn garage-btn-secondary garage-btn-sm',
          })
        : null}
      {canRouteToIT(liveStatus)
        ? adminBtn(
            isAlreadyRoutedToIT(liveStatus) ? '✓ Routed to IT' : 'Route to IT',
            () => onRouteToIT?.(),
            {
              className: 'garage-btn garage-btn-primary garage-btn-sm',
              disabled: isAlreadyRoutedToIT(liveStatus),
            }
          )
        : null}
      {config.DOT_ESCALATION_ENABLED &&
      canEscalateToDot &&
      canEscalateBizOps(liveStatus, request)
        ? adminBtn(
            request.dotPublicUuid || liveStatus === 'escalated_to_it_bizops'
              ? '✓ IT BizOps'
              : 'Escalate to IT BizOps',
            () => onEscalateToDot?.(),
            {
              className: 'garage-btn garage-btn-primary garage-btn-sm',
              disabled: Boolean(request.dotPublicUuid),
            }
          )
        : null}
      {renderSendBackControl()}
    </>
  );

  const renderForwardActions = () => {
    const targets = forwardTargets(liveStatus, request);
    return targets.map((t) => {
      if (t === 'escalated_to_it_bizops') {
        if (
          !(
            config.DOT_ESCALATION_ENABLED &&
            canEscalateToDot &&
            request.primaryPersona === 'Sales'
          )
        ) {
          return null;
        }
        return (
          <React.Fragment key={t}>
            {adminBtn(FORWARD_LABELS[t] || stageLabel(t), () => onEscalateToDot?.(), {
              className: 'garage-btn garage-btn-primary garage-btn-sm',
            })}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={t}>
          {adminBtn(
            FORWARD_LABELS[t] || `Move to ${stageLabel(t)}`,
            () => onStatusChange?.(t),
            { className: 'garage-btn garage-btn-primary garage-btn-sm' }
          )}
        </React.Fragment>
      );
    });
  };

  const renderPauseBanner = () => {
    if (!paused) return null;
    const status = liveStatus;
    let title = 'Paused';
    let detail = 'This request is paused outside the main pipeline.';
    let bannerClass = 'is-hold';
    if (status === 'needs_info') {
      title = 'Needs info';
      detail =
        'Waiting on additional information from the requestor. The workflow is held at Under review.';
      bannerClass = '';
    } else if (status === 'on_hold') {
      title = 'On hold';
      detail = 'This request is on hold. Resume when ready to continue.';
    } else if (status === 'rejected') {
      title = 'Rejected';
      detail =
        'This request was rejected. Reopen to Under review if it should continue.';
      bannerClass = 'is-rejected';
    }
    return (
      <div className={`request-flow-pause-banner ${bannerClass}`.trim()}>
        <div className="request-flow-pause-title">{title}</div>
        <p className="request-flow-pause-detail">{detail}</p>
        {canManageRequests ? (
          <div className="request-flow-pause-actions">
            {status === 'rejected' ? (
              <button
                type="button"
                className="garage-btn garage-btn-primary garage-btn-sm"
                disabled={actionsDisabled}
                onClick={() => onStatusChange?.('under_review')}
              >
                Reopen → Under review
              </button>
            ) : null}
            {status === 'on_hold' ? (
              <button
                type="button"
                className="garage-btn garage-btn-primary garage-btn-sm"
                disabled={actionsDisabled}
                onClick={() => onStatusChange?.('under_review')}
              >
                Resume under review
              </button>
            ) : null}
            {status === 'on_hold' || status === 'needs_info' ? (
              <button
                type="button"
                className="garage-btn garage-btn-primary garage-btn-sm"
                disabled={actionsDisabled}
                onClick={() => onStatusChange?.('in_progress')}
              >
                Move to In progress
              </button>
            ) : null}
            {status !== 'rejected' && canRouteToIT(status) ? (
              <button
                type="button"
                className="garage-btn garage-btn-secondary garage-btn-sm"
                disabled={actionsDisabled}
                onClick={() => onRouteToIT?.()}
              >
                Route to IT
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderSubmitted = () => {
    const hasSubmissionDetails = Boolean(
      request.requestPhase ||
        request.appType ||
        request.gemUrl ||
        request.webUrl ||
        request.rcAppUrl ||
        request.repoUrl ||
        request.deploymentPlan ||
        request.technicalDetails ||
        request.existingToolId
    );
    return (
      <PaneShell
        title="Submitted"
        state={selectedMeta.state}
        actions={
          <>
            {renderForwardActions()}
            {renderCommonSideActions()}
          </>
        }
      >
        <p className="request-flow-lede">
          Request received and waiting to enter review.
        </p>
        <dl className="request-flow-dl">
          <dt>Submitted by</dt>
          <dd>{request.requesterName || request.requesterEmail || '—'}</dd>
          <dt>Submitted</dt>
          <dd>{submittedDate || '—'}</dd>
          <dt>Personas</dt>
          <dd>{request.targetPersonas || request.primaryPersona || '—'}</dd>
          <dt>Expected reach</dt>
          <dd>{REACH_LABELS[request.reach] || request.reach || '—'}</dd>
        </dl>

        {hasSubmissionDetails ? (
          <>
            <h5 className="request-flow-section-label">Submission details</h5>
            <dl className="request-flow-dl">
              {request.requestPhase ? (
                <>
                  <dt>Request phase</dt>
                  <dd>
                    {REQUEST_PHASE_LABELS[request.requestPhase] || request.requestPhase}
                  </dd>
                </>
              ) : null}
              {request.appType ? (
                <>
                  <dt>Type</dt>
                  <dd>
                    {appTypeLabel(request.appType)}
                    {request.appType === 'app' && request.appSubtype
                      ? ` · ${request.appSubtype === 'bot' ? 'Bot' : 'Web'}`
                      : ''}
                  </dd>
                </>
              ) : null}
              {request.webUrl ? (
                <>
                  <dt>Web URL</dt>
                  <dd>
                    <a href={request.webUrl} target="_blank" rel="noopener noreferrer">
                      {request.webUrl} <ExternalLink size={12} />
                    </a>
                  </dd>
                </>
              ) : null}
              {request.rcAppUrl ? (
                <>
                  <dt>RC App URL</dt>
                  <dd>
                    <a href={request.rcAppUrl} target="_blank" rel="noopener noreferrer">
                      {request.rcAppUrl} <ExternalLink size={12} />
                    </a>
                  </dd>
                </>
              ) : null}
              {request.gemUrl ? (
                <>
                  <dt>Gem URL</dt>
                  <dd>
                    <a href={request.gemUrl} target="_blank" rel="noopener noreferrer">
                      {request.gemUrl} <ExternalLink size={12} />
                    </a>
                  </dd>
                </>
              ) : null}
              {request.repoUrl ? (
                <>
                  <dt>Repository</dt>
                  <dd>
                    <a href={request.repoUrl} target="_blank" rel="noopener noreferrer">
                      {request.repoUrl} <ExternalLink size={12} />
                    </a>
                  </dd>
                </>
              ) : null}
              {request.existingToolId ? (
                <>
                  <dt>Existing tool</dt>
                  <dd>
                    {request.existingToolTitle ? (
                      <a href={`/ai-garage/discover/${request.existingToolId}`}>
                        {request.existingToolTitle} <ExternalLink size={12} />
                      </a>
                    ) : (
                      `Tool ID: ${request.existingToolId}`
                    )}
                  </dd>
                </>
              ) : null}
            </dl>
            {request.deploymentPlan ? (
              <>
                <h5 className="request-flow-section-label">Deployment plan</h5>
                <div className="request-flow-blurb">{request.deploymentPlan}</div>
              </>
            ) : null}
            {request.technicalDetails ? (
              <>
                <h5 className="request-flow-section-label">Technical details</h5>
                <div className="request-flow-blurb">{request.technicalDetails}</div>
              </>
            ) : null}
          </>
        ) : null}

        <h5 className="request-flow-section-label">
          <Paperclip size={14} /> Attachments ({attachments.length})
        </h5>
        {attachments.length === 0 ? (
          <p className="request-flow-muted">No files attached yet.</p>
        ) : (
          <ul className="request-flow-attachment-list">
            {attachments.map((attachment) => {
              const isUploader =
                (attachment.uploaderEmail || '').toLowerCase() ===
                (userEmail || '').toLowerCase();
              const canDelete =
                isUploader || String(userRole || '').toLowerCase() === 'admin';
              return (
                <li className="request-flow-attachment-row" key={attachment.id}>
                  <span className="request-flow-attachment-icon" aria-hidden>
                    <Paperclip size={15} />
                  </span>
                  <div className="request-flow-attachment-info">
                    <strong title={attachment.filename}>{attachment.filename}</strong>
                    <span>
                      {formatAttachmentSize(attachment.sizeBytes)} · Added by{' '}
                      {attachment.uploaderName || attachment.uploaderEmail}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="request-flow-attachment-btn"
                    onClick={() => onDownloadAttachment?.(attachment)}
                    disabled={downloadingAttachment === attachment.id}
                    aria-label={`Download ${attachment.filename}`}
                    title="Download"
                  >
                    <Download size={15} />
                  </button>
                  {canDelete ? (
                    <button
                      type="button"
                      className="request-flow-attachment-btn is-danger"
                      onClick={() => onDeleteAttachment?.(attachment)}
                      aria-label={`Delete ${attachment.filename}`}
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </PaneShell>
    );
  };

  const renderUnderReview = () => (
    <PaneShell
      title="Under review"
      state={selectedMeta.state}
      actions={
        <>
          {renderForwardActions()}
          {renderCommonSideActions()}
        </>
      }
    >
      <p className="request-flow-lede">
        AI Garage is reviewing fit, priority, and next steps.
      </p>
      <ReviewerBlock reviewerData={selectedMeta.reviewerData} />
      {normalizeStatus(request.status) === 'needs_info' ? (
        <p className="request-flow-lede" style={{ color: 'var(--request-warning-text, #b45309)' }}>
          Currently flagged as Needs info — waiting on the requestor.
        </p>
      ) : null}
      <h5 className="request-flow-section-label">Handoff notes</h5>
      <HandoffComments entries={stageHandoffComments(activity, 'under_review')} />
    </PaneShell>
  );

  const renderInProgress = () => (
    <PaneShell
      title="In progress"
      state={selectedMeta.state}
      actions={
        <>
          {renderForwardActions()}
          {renderCommonSideActions()}
        </>
      }
    >
      <p className="request-flow-lede">Build or refinement work is underway.</p>
      <ReviewerBlock reviewerData={selectedMeta.reviewerData} />
      <dl className="request-flow-dl">
        <dt>Assigned team</dt>
        <dd>{request.assignedTeam || selectedMeta.reviewerData?.team || '—'}</dd>
        <dt>Maker</dt>
        <dd>
          {request.assignedMakerName ||
            request.assignedMakerEmail ||
            selectedMeta.reviewerData?.name ||
            '—'}
        </dd>
        <dt>App type</dt>
        <dd>
          {request.appType
            ? `${request.appType}${request.appSubtype ? ` · ${request.appSubtype}` : ''}`
            : '—'}
        </dd>
        <dt>Repository</dt>
        <dd>
          {request.repoUrl ? (
            <a href={request.repoUrl} target="_blank" rel="noopener noreferrer">
              {request.repoUrl} <ExternalLink size={12} />
            </a>
          ) : (
            '—'
          )}
        </dd>
      </dl>
      <h5 className="request-flow-section-label">Handoff notes</h5>
      <HandoffComments entries={stageHandoffComments(activity, 'in_progress')} />
    </PaneShell>
  );

  const downloadItArtifact = async (jobId, kind, fileName) => {
    if (!jobId) return;
    const key = `${jobId}:${kind}`;
    try {
      setArtifactBusy(key);
      await garageAPI.downloadSecScanArtifact(jobId, kind, fileName);
    } catch (err) {
      console.error('Artifact download failed:', err);
    } finally {
      setArtifactBusy(null);
    }
  };

  const renderOptimization = () => {
    const it = itReviewPayload?.itReview || itReviewPayload;
    const security = it?.security;
    const codeReview = it?.codeReview;
    const optComments = stageHandoffComments(activity, 'optimization');
    const secArtifacts = security?.artifacts || security?.summary?.artifacts || [];
    const codeArtifacts = codeReview?.artifacts || codeReview?.summary?.artifacts || [];

    return (
      <PaneShell
        title="Optimization"
        state={selectedMeta.state}
        actions={
          <>
            {renderForwardActions()}
            {renderCommonSideActions()}
          </>
        }
      >
        <p className="request-flow-lede">
          Requestor fix stage — address feedback from IT review (or polish before handoff), then
          advance or Route to IT again.
        </p>
        <ReviewerBlock reviewerData={selectedMeta.reviewerData} />

        {(security || codeReview) && (
          <>
            <h5 className="request-flow-section-label">IT review findings</h5>
            <dl className="request-flow-dl">
              {security ? (
                <>
                  <dt>Security</dt>
                  <dd>
                    {security.status || '—'}
                    {security.summary?.grade || security.grade
                      ? ` · Grade ${security.summary?.grade || security.grade}`
                      : ''}
                  </dd>
                </>
              ) : null}
              {codeReview ? (
                <>
                  <dt>Code &amp; arch</dt>
                  <dd>
                    {codeReview.status || '—'}
                    {codeReview.summary?.verdict || codeReview.verdict
                      ? ` · ${codeReview.summary?.verdict || codeReview.verdict}`
                      : ''}
                  </dd>
                </>
              ) : null}
            </dl>
            {[
              { job: security, label: 'Security artifacts', arts: secArtifacts },
              { job: codeReview, label: 'Code artifacts', arts: codeArtifacts },
            ].map(({ job, label, arts }) =>
              job && arts?.length ? (
                <div key={label} style={{ marginBottom: '0.65rem' }}>
                  <h5 className="request-flow-section-label">{label}</h5>
                  <div className="request-flow-actions">
                    {arts.map((a) => {
                      const kind = a.kind || a.fileName;
                      const busyKey = `${job.id}:${kind}`;
                      return (
                        <button
                          key={kind}
                          type="button"
                          className="garage-btn garage-btn-secondary garage-btn-sm"
                          disabled={artifactBusy === busyKey}
                          onClick={() => downloadItArtifact(job.id, kind, a.fileName)}
                        >
                          <Download size={14} /> {a.fileName || kind}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null
            )}
          </>
        )}

        <h5 className="request-flow-section-label">Handoff notes</h5>
        <HandoffComments
          entries={optComments}
          emptyHint="No handoff comment yet (e.g. from IT review)."
        />
        {request.deploymentPlan ? (
          <>
            <h5 className="request-flow-section-label">Deployment plan</h5>
            <div className="request-flow-blurb">{truncate(request.deploymentPlan, 360)}</div>
          </>
        ) : null}
      </PaneShell>
    );
  };

  const renderBizOps = () => {
    if (!isSalesPersona(request) && liveStatus !== 'escalated_to_it_bizops') {
      return (
        <PaneShell title="IT BizOps" state={selectedMeta.state}>
          <p className="request-flow-muted">IT BizOps applies to Sales persona requests only.</p>
        </PaneShell>
      );
    }
    return (
      <PaneShell
        title="IT BizOps"
        state={selectedMeta.state}
        actions={
          <>
            {request.dotPublicUrl
              ? adminBtn('Open Sales / DOT ticket', () => window.open(request.dotPublicUrl, '_blank'), {
                  className: 'garage-btn garage-btn-primary garage-btn-sm',
                })
              : null}
            {renderForwardActions()}
            {renderCommonSideActions()}
          </>
        }
      >
        <p className="request-flow-lede">
          Sales intake with IT Business Operations (DOT). Escalate anytime; Route to IT when ready
          for security scans.
        </p>
        <ReviewerBlock reviewerData={selectedMeta.reviewerData} />
        <dl className="request-flow-dl">
          <dt>DOT reference</dt>
          <dd>{request.dotRefNumber || '—'}</dd>
          <dt>Public status</dt>
          <dd>
            {request.dotPublicUrl ? (
              <a href={request.dotPublicUrl} target="_blank" rel="noopener noreferrer">
                View Sales ticket status <ExternalLink size={12} />
              </a>
            ) : (
              '—'
            )}
          </dd>
          <dt>DOT UUID</dt>
          <dd>{request.dotPublicUuid || '—'}</dd>
          <dt>Escalated by</dt>
          <dd>{request.escalatedBy || selectedMeta.reviewerData?.name || '—'}</dd>
          <dt>Escalated at</dt>
          <dd>
            {request.escalatedAt
              ? new Date(request.escalatedAt).toLocaleString()
              : '—'}
          </dd>
        </dl>
        <h5 className="request-flow-section-label">Handoff notes</h5>
        <HandoffComments
          entries={stageHandoffComments(activity, 'escalated_to_it_bizops')}
        />
      </PaneShell>
    );
  };

  /** Parent Deploying stage — publish gate + status. Not the IT subflow Deployment node. */
  const renderDeploying = () => {
    const it = itReviewPayload?.itReview || itReviewPayload;
    const nonRepo = Boolean(request?.itScansNotApplicable || it?.itScansNotApplicable);
    const deployment = it?.deployment || itReviewPayload?.deployment;
    const pipelineUrl = deployment?.pipelineUrl || deployment?.pipeline_url;
    const pipelineStatus = deployment?.pipelineStatus || deployment?.uiStatus || deployment?.status;
    const pipelineOk = ['succeeded', 'success', 'completed'].includes(
      String(pipelineStatus || '').toLowerCase()
    );
    const itLinked = needsItScanPanel(request) && !nonRepo;
    const publishReady = canPublishFromStatus(liveStatus, request, itReviewPayload);
    const deployedUrl = isGemRequest(request)
      ? request?.gemUrl
      : request?.webUrl || request?.gemUrl;
    const checks = nonRepo
      ? [
          { done: true, label: 'IT review complete' },
          {
            done: normalizeStatus(request.status) === 'deploying' || publishReady,
            label: 'Request moved to Deploying',
          },
          {
            done: Boolean((deployedUrl || '').trim()),
            label: 'Deployed app URL recorded',
          },
          {
            done: normalizeStatus(request.status) === 'published',
            label: 'Published',
          },
        ]
      : [
          { done: itLinked, label: 'IT review linked (scans / garage-apps)' },
          { done: Boolean(pipelineUrl || deployment?.pipelineId), label: 'IT pipeline recorded' },
          { done: pipelineOk, label: 'IT pipeline succeeded' },
          {
            done: normalizeStatus(request.status) === 'deploying' || publishReady,
            label: 'Request moved to Deploying',
          },
          {
            done: normalizeStatus(request.status) === 'published',
            label: 'Ready to publish / published',
          },
        ];

    return (
      <PaneShell
        title="Deploying"
        state={selectedMeta.state}
        actions={
          <>
            {publishReady &&
              canPublishGem &&
              !isGemPublished &&
              adminBtn('Publish Gem', () => onPublishGem?.(), {
                className: 'garage-btn garage-btn-primary garage-btn-sm',
              })}
            {publishReady &&
              !isGemRequest(request) &&
              adminBtn('Mark published', () => onStatusChange?.('published'), {
                className: 'garage-btn garage-btn-primary garage-btn-sm',
              })}
            {!publishReady && actionsLiveHere ? (
              <span className="request-flow-muted">
                Publish unlocks when prior stages and IT gates are complete.
              </span>
            ) : null}
            {renderSendBackControl()}
            {canReject(liveStatus)
              ? adminBtn('Reject', () => onStatusChange?.('rejected'), {
                  className: 'garage-btn garage-btn-secondary garage-btn-sm',
                })
              : null}
          </>
        }
      >
        <p className="request-flow-lede">
          {nonRepo
            ? 'IT review is complete. Confirm the deployed app, then publish.'
            : 'Parent release stage — confirm readiness and publish. Pipeline initiation and deployment todos live under IT review → Deployment, not here.'}
        </p>
        <ReviewerBlock reviewerData={selectedMeta.reviewerData} />

        <h5 className="request-flow-section-label">Publish readiness</h5>
        <ul className="request-flow-checklist">
          {checks.map((c) => (
            <li key={c.label}>
              <span className={`request-flow-check${c.done ? ' is-done' : ''}`} aria-hidden />
              <span>{c.label}</span>
            </li>
          ))}
        </ul>

        {nonRepo ? (
          <dl className="request-flow-dl">
            <dt>Deployed app URL</dt>
            <dd>
              {deployedUrl ? (
                <a href={deployedUrl} target="_blank" rel="noopener noreferrer">
                  {deployedUrl} <ExternalLink size={12} />
                </a>
              ) : (
                'Not provided'
              )}
            </dd>
          </dl>
        ) : itLinked ? (
          <>
            <h5 className="request-flow-section-label">IT pipeline (read-only)</h5>
            <dl className="request-flow-dl">
              <dt>Status</dt>
              <dd>{pipelineStatus || 'Not started / waiting'}</dd>
              <dt>Pipeline</dt>
              <dd>
                {pipelineUrl ? (
                  <a href={pipelineUrl} target="_blank" rel="noopener noreferrer">
                    Open GitLab pipeline <ExternalLink size={12} />
                  </a>
                ) : (
                  '—'
                )}
              </dd>
              {deployment?.error ? (
                <>
                  <dt>Error</dt>
                  <dd>{deployment.error}</dd>
                </>
              ) : null}
            </dl>
            <p className="request-flow-muted">
              To run or re-run IT deployment, open the <strong>IT review</strong> stage and use
              its Deployment step.
            </p>
          </>
        ) : (
          <p className="request-flow-muted">
            No IT scan link on this request — publish when your release checklist is done.
          </p>
        )}

        <h5 className="request-flow-section-label">Handoff notes</h5>
        <HandoffComments entries={stageHandoffComments(activity, 'deploying')} />
      </PaneShell>
    );
  };

  const renderPublished = () => (
    <PaneShell
      title="Published"
      state={selectedMeta.state}
      actions={
        <>
          {request.existingToolId
            ? adminBtn(
                'View in Discover',
                () => {
                  window.location.href = `/ai-garage/discover/${request.existingToolId}`;
                },
                { className: 'garage-btn garage-btn-primary garage-btn-sm' }
              )
            : null}
          {request.gemUrl
            ? adminBtn('Open Gem URL', () => window.open(request.gemUrl, '_blank'))
            : null}
        </>
      }
    >
      <p className="request-flow-lede">
        {isGemPublished
          ? 'This request is live as a Gem in Discover.'
          : 'Request marked published.'}
      </p>
      <ReviewerBlock reviewerData={selectedMeta.reviewerData} />
      <dl className="request-flow-dl">
        <dt>Discover</dt>
        <dd>
          {request.existingToolId ? (
            <a href={`/ai-garage/discover/${request.existingToolId}`}>
              {request.existingToolTitle || 'View in Discover'} <ExternalLink size={12} />
            </a>
          ) : (
            '—'
          )}
        </dd>
        <dt>Gem URL</dt>
        <dd>
          {request.gemUrl ? (
            <a href={request.gemUrl} target="_blank" rel="noopener noreferrer">
              {request.gemUrl} <ExternalLink size={12} />
            </a>
          ) : (
            '—'
          )}
        </dd>
        <dt>Web URL</dt>
        <dd>
          {request.webUrl ? (
            <a href={request.webUrl} target="_blank" rel="noopener noreferrer">
              {request.webUrl} <ExternalLink size={12} />
            </a>
          ) : (
            '—'
          )}
        </dd>
      </dl>
      <h5 className="request-flow-section-label">Handoff notes</h5>
      <HandoffComments entries={stageHandoffComments(activity, 'published')} />
    </PaneShell>
  );

  const renderItReview = () => {
    if (showItEmbed) {
      return (
        <>
          {canManageRequests && isActivePane ? (
            <div className="request-flow-pane" style={{ marginBottom: '0.85rem' }}>
              <div className="request-flow-pane-head">
                <h4>IT review</h4>
                <span className={`request-flow-status-pill ${pillTone(selectedMeta.state)}`}>
                  {pillLabel(selectedMeta.state)}
                </span>
              </div>
              <p className="request-flow-lede">
                Security, code review, and IT Deployment run in the panel below. Parent{' '}
                <strong>Deploying</strong> is a separate publish stage after this.
              </p>
              <div className="request-flow-actions">
                {renderForwardActions()}
                {renderSendBackControl()}
                {canReject(liveStatus)
                  ? adminBtn('Reject', () => onStatusChange?.('rejected'), {
                      className: 'garage-btn garage-btn-secondary garage-btn-sm',
                    })
                  : null}
              </div>
            </div>
          ) : null}
          <ItScanFlowPanel
            requestId={requestId}
            request={request}
            initialPayload={itReviewPayload}
            isAdmin={canManageRequests}
            userEmail={userEmail}
            userName={userName}
            onRequestRefresh={onRequestRefresh}
            focusInitiated={focusItInitiated}
            onSendToOptimization={
              canManageRequests
                ? () => onStatusChange?.('optimization')
                : undefined
            }
          />
        </>
      );
    }
    return (
      <PaneShell
        title="IT review"
        state={selectedMeta.state}
        actions={
          <>
            {renderForwardActions()}
            {renderCommonSideActions()}
          </>
        }
      >
        <p className="request-flow-lede">
          {normalizeStatus(request.status) === 'it_review'
            ? 'IT review is active. Security and IT Deployment details will appear here once the IT payload loads.'
            : 'Route this request to IT to start security scans, garage-apps copy, and IT Deployment.'}
        </p>
        <ReviewerBlock reviewerData={selectedMeta.reviewerData} />
        <dl className="request-flow-dl">
          <dt>Repository</dt>
          <dd>
            {request.repoUrl ? (
              <a href={request.repoUrl} target="_blank" rel="noopener noreferrer">
                {request.repoUrl} <ExternalLink size={12} />
              </a>
            ) : (
              'Not set'
            )}
          </dd>
          <dt>Sec-scan repo</dt>
          <dd>{request.secScanRepoId || '—'}</dd>
        </dl>
        <h5 className="request-flow-section-label">Handoff notes</h5>
        <HandoffComments entries={stageHandoffComments(activity, 'it_review')} />
      </PaneShell>
    );
  };

  const renderPane = () => {
    switch (selectedStage) {
      case 'submitted':
        return renderSubmitted();
      case 'under_review':
        return renderUnderReview();
      case 'in_progress':
        return renderInProgress();
      case 'optimization':
        return renderOptimization();
      case 'escalated_to_it_bizops':
        return renderBizOps();
      case 'it_review':
        return renderItReview();
      case 'deploying':
        return renderDeploying();
      case 'published':
        return renderPublished();
      default:
        return renderSubmitted();
    }
  };

  return (
    <section className="request-flow-panel" aria-label="Request workflow">
      <h3 className="request-flow-subtitle">Request workflow</h3>
      {renderPauseBanner()}
      <div className="request-flow-layout">
        <aside className="request-flow-rail" aria-label="Request stages">
          <ol className="request-flow-v-timeline">
            {timeline.map((stage, index) => {
              const state = stage.state || 'pending';
              const selected = selectedStage === stage.id;
              const next = timeline[index + 1];
              const pathContinues =
                state === 'completed' &&
                next &&
                (next.state === 'completed' || next.state === 'active');
              return (
                <li
                  key={stage.id}
                  className={`request-flow-v-step ${state}${
                    pathContinues ? ' path-continues' : ''
                  }${selected ? ' is-selected' : ''}`}
                >
                  {index < timeline.length - 1 && (
                    <span className="request-flow-v-connector" aria-hidden />
                  )}
                  <button
                    type="button"
                    className="request-flow-v-btn"
                    onClick={() => selectStage(stage.id)}
                    aria-pressed={selected}
                  >
                    <span className="request-flow-v-dot" />
                    <span className="request-flow-v-body">
                      <span className="request-flow-v-label">{stage.label}</span>
                      {stage.reviewerData?.name && state !== 'pending' ? (
                        <span className="request-flow-v-meta">{stage.reviewerData.name}</span>
                      ) : null}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>
        <div
          className={`request-flow-pane-wrap${
            showItEmbed && selectedStage === 'it_review' ? ' is-embedded-it' : ''
          }`}
        >
          {renderPane()}
        </div>
      </div>
    </section>
  );
};

export default RequestWorkflowPanel;
export { needsItScanPanel, WORKFLOW_STAGES };
